import {makeAutoObservable, observable} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {TypeUtil} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerValue,
  GenericStickerState,
  Hub,
  StickerColor,
  SystemStickerType,
  systemStickerTypes,
  Tag,
  TimerStickerValue
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {DragDataObject} from '@common/drag-data'
import {Tracking, TrackingEvent} from '@common/tracking'
import {TippyInstance} from '@components/common/dropdown/dropdown'
import {MessageBox} from '@components/message-box'
import {StickerPanelLocation} from '@components/sticker/sticker-panel/sticker-panel-store'
import {
  StickerData,
  StickerItemProps,
  StickerState
} from '@components/sticker/types'
import {createUseStore} from '@containers/hooks'
import {GanttDraft} from '@extensions/gantt-draft'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Sticker as StickerExtension} from '@extensions/sticker/sticker'
import {StickerFilter} from '@extensions/sticker/sticker-filter'
import {
  toServerDeadline,
  toVisualDeadline
} from '@extensions/sticker/views-web/deadline/deadline-sticker-util'
import {Task} from '@extensions/task'
import {deadlineTimerView} from './sticker-menu/sticker-menu-deadline/utils'
import {getIsRunningTimer, getStickerClassName, getStickerTitle} from './utils'
import {getStickerColor} from './utils/get-sticker-color'

export class StickerStore {
  state: StickerState
  stickerData: StickerData
  filtersController?: StickerFilter
  isTaskTemplate?: boolean
  ganttDraftUnsubscribe?: () => void
  onPinHandler
  onUnpinHandler
  onCloseMenu
  onCreateMenuHandler

  constructor(
    public sticker: Tag,
    stickerProps: StickerItemProps
  ) {
    this.onPinHandler = stickerProps.onPin
    this.onUnpinHandler = stickerProps.onUnpin
    this.onCloseMenu = stickerProps.onCloseMenu
    this.onCreateMenuHandler = stickerProps.onCreateStickerMenu
    const stickerType = StickerExtension.getType(sticker)
    const currentBoard = PageService.getCurrentBoard()
    this.stickerData = {
      stickerType,
      location: stickerProps.location,
      isInBoard: stickerProps.isInBoard,
      isSystem:
        systemStickerTypes.includes(stickerType as SystemStickerType) || false,
      icon: StickerExtension.getIcon(sticker),
      draggable: Boolean(stickerProps.draggable),
      withMenu: Boolean(stickerProps.withMenu),
      withTooltip: Boolean(stickerProps.withTooltip),
      disabled: stickerProps.disabled,
      className: stickerProps.className,
      closeMenuAfterPin: stickerProps.closeMenuAfterPin || false,
      isAddingTask: stickerProps.isAddingTask
    }
    this.filtersController =
      stickerProps.filtersController ||
      (currentBoard && BoardFilters.stickerFilterFromBoard(currentBoard))

    this.isTaskTemplate = stickerProps.isTaskTemplate

    this.state = {
      isRunningTimer: false,
      taskId: stickerProps.taskId,
      menuIsOpened: stickerProps.menuIsOpened,
      unpinning: false,
      isDragging: false,
      hasError: Boolean(stickerProps.hasError),
      isEditable:
        this.stickerTask && Task.isTask(this.stickerTask)
          ? PermissionsChecker.checkEditTaskStickers(
              this.stickerTask,
              this.sticker,
              undefined
            )
          : true,
      settings: currentBoard?.data.stickersSettings?.[this.sticker.id],
      displayedTitle: '',
      location: stickerProps.location,
      parentLocation: stickerProps.parentLocation
    }

    this.syncStickerIsRequired(stickerProps.required)
    this.syncDefaultValue()
    this.syncStickerTitle()
    this.syncStickerHasTransparentStyle()
    this.syncTaskIsCompleted()
    this.syncOnUpdateFilters()
    this.syncDisplayedTitle()

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        stickerData: false,
        onPinHandler: false,
        onUnpinHandler: false,
        ganttDraftUnsubscribe: false
      },
      {autoBind: true}
    )

    if (stickerType === 'deadline') {
      this.ganttDraftUnsubscribe = GanttDraft.subscribe(this.syncDisplayedTitle)
    }
  }

  syncStickerIsRequired(isRequired?: boolean): void {
    this.state.required = isRequired
  }

  syncParentLocation(parentLocation?: StickerPanelLocation): void {
    this.state.parentLocation = parentLocation
  }

  syncIsRunningTimer(): void {
    const {stickerType, location} = this.stickerData
    const {
      value: stickerValue,
      defaultValue: stickerDefaultValue,
      settings
    } = this.state

    const isRunningTimer = getIsRunningTimer({
      stickerType,
      location,
      stickerValue,
      stickerDefaultValue,
      task: this.stickerTask,
      settings
    })
    this.setIsRunningTimer(isRunningTimer)
  }

  dispose(): void {
    clearInterval(this.state.runningTimer)
    this.ganttDraftUnsubscribe?.()
  }

  setHasError(value?: boolean): void {
    this.state.hasError = value
  }

  setIsRunningTimer(value: boolean): void {
    if (this.state.isRunningTimer !== value) {
      this.state.isRunningTimer = value
      this.updateTimer()
    }
  }

  private updateTimer(): void {
    clearInterval(this.state.runningTimer)
    if (!this.state.isRunningTimer) {
      return
    }

    if (
      ['stopwatch', 'timer'].includes(this.stickerData.stickerType) ||
      (this.stickerData.stickerType === 'deadline' &&
        this.stickerTask &&
        deadlineTimerView(
          this.stickerTask,
          this.state.defaultValue as DeadlineStickerValue,
          this.state.settings
        ))
    ) {
      this.state.runningTimer = setInterval(() => {
        this.setDefaultValue({...(this.state.value as TimerStickerValue)}, true)
      }, 1000)
    }
  }

  syncOnUpdateBoard(): void {
    const board = PageService.getCurrentBoard()
    this.state.settings = board?.data.stickersSettings?.[this.sticker.id]
    if (board) {
      this.filtersController = BoardFilters.stickerFilterFromBoard(board)
    }
    this.syncDisplayedTitle()
  }

  syncOnUpdateFilters(): void {
    this.state.filterValues = this.filtersController?.getChosen(this.sticker.id)
    this.syncDisplayedTitle()
  }

  setStickerTaskId(stickerId?: DataObjectId): void {
    this.state.taskId = stickerId

    if (this.stickerTask && Task.isTask(this.stickerTask)) {
      this.syncDefaultValue()
    }
  }

  syncDefaultValue(): void {
    const stickerValue =
      this.stickerData.location === 'task'
        ? (this.stickerTask?.stickers?.[this.sticker.id] as GenericStickerState)
        : undefined

    if (
      this.state.menuIsOpened &&
      ['numeric', 'text'].includes(this.stickerData.stickerType) &&
      this.state.value !== stickerValue
    ) {
      // don't update value if it's updated by user already, see DEV-3515
      return
    }

    this.setDefaultValue(stickerValue)
  }

  syncStickerTitle(): void {
    this.state.title = this.sticker.title
  }

  syncDisplayedTitle(): void {
    this.state.displayedTitle = this.prepareDisplayedTitle()
  }

  syncStickerHasTransparentStyle(): void {
    const stickerTask = this.stickerTask

    this.state.hasTransparentStyle =
      stickerTask &&
      this.stickerData.isInBoard &&
      this.stickerData.location === 'task' &&
      Task.isTaskColored(stickerTask)
  }

  syncTaskIsCompleted(): void {
    this.state.taskIsCompleted =
      this.stickerTask && Task.isDone(this.stickerTask)
  }

  toggleStickerMenu(): void {
    this.state.menuIsOpened = !this.state.menuIsOpened
    if (!this.state.menuIsOpened && this.onCloseMenu) {
      this.onCloseMenu(this.sticker)
    }
  }

  tryPinAndCloseMenu(): void {
    if (['text', 'numeric'].includes(this.stickerData.stickerType)) {
      this.pinStickerValue()
    }
    this.closeStickerMenu()
  }

  tryCancelAndCloseMenu(): void {
    if (['text', 'numeric'].includes(this.stickerData.stickerType)) {
      this.syncDefaultValue()
    }
    this.closeStickerMenu()
  }

  closeStickerMenu(): void {
    this.state.menuIsOpened = false
    this.onCloseMenu?.(this.sticker)
  }

  setStickerMenuOpened(value: boolean): void {
    this.state.menuIsOpened = value
  }

  prepareStickerValue(
    value?: GenericStickerState
  ): GenericStickerState | undefined {
    const valueAsTimer = value as TimerStickerValue
    if (valueAsTimer && Array.isArray(valueAsTimer.history)) {
      valueAsTimer.history = valueAsTimer.history.filter(TypeUtil.isTruthy)
    }
    return value
  }

  setDefaultValue(
    defaultValue?: GenericStickerState,
    visualDeadline = false
  ): void {
    if (
      !visualDeadline &&
      this.stickerTask &&
      this.stickerData.stickerType === 'deadline'
    ) {
      defaultValue = toVisualDeadline(
        this.stickerTask,
        defaultValue as DeadlineStickerValue
      )
    }

    const preparedValue = this.prepareStickerValue(defaultValue)
    this.state.defaultValue = preparedValue
    this.state.value = preparedValue
    this.syncDisplayedTitle()
  }

  setStickerValue(value: GenericStickerState): void {
    if (
      this.stickerTask &&
      Task.isTask(this.stickerTask) &&
      !PermissionsChecker.checkEditTaskStickers(
        this.stickerTask,
        this.sticker,
        undefined
      )
    ) {
      MessageBox.warn(Lang.msg.permissionsForbiddenTaskStickers)
      return
    } else {
      this.state.value = this.prepareStickerValue(value)
      this.syncDisplayedTitle()
    }
  }

  startDragging(el?: HTMLElement): void {
    this.state.isDragging = true

    if (el && !this.state.hasTransparentStyle) {
      el.style.outline = 'unset'
    }
  }

  endDragging(el?: HTMLElement): void {
    this.state.isDragging = false

    if (el && !this.state.hasTransparentStyle) {
      el.style.outline = ''
    }
  }

  private trackFilterChanges(oldValues: string[], newValues: string[]): void {
    // Track only when values are added to a filter
    if (newValues.length <= oldValues.length) {
      return
    }

    // Track filter changes only on board
    if (
      this.state.location === 'panel' &&
      this.state.parentLocation === 'board'
    ) {
      const type = this.stickerData.stickerType
      if (type === 'user') {
        void Tracking.send(
          TrackingEvent.stickerPanelApplyFilteringByTheAssignedSticker
        )
      } else if (type === 'deadline') {
        void Tracking.send(
          TrackingEvent.stickerPanelApplyFilteringByTheDeadlineSticker
        )
      } else if (type === 'sprint') {
        void Tracking.send(
          TrackingEvent.stickerPanelApplyFilteringByTheTimeIntervalSticker
        )
      } else if (type === 'string') {
        void Tracking.send(
          TrackingEvent.stickerPanelApplyFilteringByTheStatusSticker
        )
      }
    }
  }

  toggleFilter(values: (string | DataObjectId)[]): void {
    const oldValues = this.state.filterValues ?? []

    values.forEach(value => {
      this.filtersController?.onToggle(this.sticker.id, value)
    })
    this.syncOnUpdateFilters()

    const newValues = this.state.filterValues ?? []
    this.trackFilterChanges(oldValues, newValues)
  }

  setFilters(values: (string | DataObjectId)[]): void {
    const oldValues = this.state.filterValues ?? []

    this.filtersController?.setFilter(this.sticker.id, values)
    this.syncOnUpdateFilters()

    const newValues = this.state.filterValues ?? []
    this.trackFilterChanges(oldValues, newValues)
  }

  get stickerTask(): Hub | undefined {
    return this.state?.taskId && Task.getById(this.state.taskId)
  }

  get color(): StickerColor {
    const {
      filterValues,
      value: stickerValue,
      defaultValue: stickerDefaultValue,
      hasTransparentStyle
    } = this.state
    const {stickerType, location} = this.stickerData

    return getStickerColor({
      sticker: this.sticker,
      stickerType,
      stickerValue,
      stickerDefaultValue,
      filterValues,
      location,
      hasTransparentStyle,
      task: this.stickerTask
    })
  }

  get name(): string {
    const {stickerType, isSystem} = this.stickerData

    return isSystem || !this.state.title
      ? Lang.msg.stickerNames[stickerType as SystemStickerType]
      : this.state.title
  }

  get titleIsBold(): boolean {
    return (
      ['stopwatch', 'timer'].includes(this.stickerData.stickerType) &&
      (this.state.value as TimerStickerValue)?.current?.running
    )
  }

  prepareDisplayedTitle(): React.ReactNode {
    const {
      filterValues,
      value: originalStickerValue,
      defaultValue: stickerDefaultValue,
      settings
    } = this.state
    const {stickerType, location} = this.stickerData

    let stickerValue = originalStickerValue
    if (stickerType === 'deadline' && this.stickerTask) {
      stickerValue =
        GanttDraft.getDraft(this.stickerTask.id)?.deadline ??
        originalStickerValue
    }

    return getStickerTitle({
      sticker: this.sticker,
      stickerType,
      stickerValue,
      stickerDefaultValue,
      filterValues,
      location,
      task: this.stickerTask,
      name: this.name,
      settings
    })
  }

  onCreateMenu(tippy: TippyInstance): void {
    this.onCreateMenuHandler?.(tippy)
  }

  unpinSticker(): void {
    if (this.onUnpinHandler) {
      this.onUnpinHandler(this.sticker)
      this.toggleStickerMenu()
      return
    }
    const task = this.stickerTask
    if (task) {
      StickerExtension.unpin(this.sticker, task)
    }
    this.toggleStickerMenu()
  }

  pinStickerValue(): void {
    let value = this.state.value
    if (this.stickerTask && this.stickerData.stickerType === 'deadline') {
      value = toServerDeadline(this.stickerTask, value as DeadlineStickerValue)
    }

    if (this.onPinHandler) {
      this.onPinHandler(value)
      return
    }

    const taskId = this.state.taskId
    const task = taskId && Task.getById(taskId)
    if (taskId && task) {
      StickerExtension.pin(this.sticker, taskId, task, value)
    }

    if (this.stickerData.closeMenuAfterPin) {
      this.toggleStickerMenu()
    }
  }

  get dragData(): DragDataObject {
    const dragData: DragDataObject = {
      type: 'sticker',
      stickerId: this.sticker.id
    }
    const {stickerType} = this.stickerData

    if (stickerType === 'deadline') {
      dragData.stateId = this.state.value as DeadlineStickerValue
    }

    if (['string', 'text', 'numeric', 'sprint'].includes(stickerType)) {
      dragData.value = this.state.value as string
    }

    if (stickerType === 'user') {
      dragData.value = this.state.value as DataObjectId
    }

    return dragData
  }

  get className(): string {
    return getStickerClassName({
      ...this.state,
      ...this.stickerData
    })
  }

  get isActive(): boolean {
    const {isEditable, menuIsOpened, filterValues} = this.state
    const {withMenu, location} = this.stickerData
    return (
      withMenu &&
      isEditable &&
      (menuIsOpened || (location === 'panel' && Boolean(filterValues?.length)))
    )
  }

  get noValue(): boolean {
    return !this.state.value
  }
}

export const [StickerContext, useStickerStore] = createUseStore(StickerStore)
